import FileSaver from "file-saver";

import { buildIcsFileContent } from "./ics-builder";

/*
 * Business Requirement:
 * OQG3-2559: on iOS only Safari (and Mail) can open a calendar file straight
 * into the Calendar app. In Chrome, Firefox, Edge and other iOS browsers
 * add-to-calendar-button gives up and shows an "Open Safari" note. Instead
 * of the note, the page downloads the .ics itself so the visitor at least
 * holds the file (iOS Quick Look previews it read-only; adding it needs
 * Safari, Mail or a drag into the Calendar app - accepted for this ticket).
 * On Safari the library (3.x) puts an extra "Add all" modal in front of
 * the file; the multi-date button must hand the file over straight away, so
 * Safari bypasses the library as well. In-app browsers (Instagram, Facebook,
 * ...) cannot save downloads at all, so they keep the library's note.
 *
 * Technical Detail:
 * iOS and webview detection mirror the library. Safari detection does not:
 * see isSafari(). Safari receives the same data: URL anchor click
 * add-to-calendar-button 2.9.1 used, which Safari opens in Calendar; other
 * browsers receive a blob download into Files.
 */

const ICS_MIME_TYPE = "text/calendar;charset=utf-8";
const ICS_DATA_URL_PREFIX = `data:${ICS_MIME_TYPE},`;
const ICS_FILE_EXTENSION = ".ics";
const ICS_FILE_NAME_FALLBACK = "event";

/* add-to-calendar-button markup: every picker entry is a `.atcb-list-item`
 * whose id ends in the calendar type; the modal close control is
 * `.atcb-list-item-close`. */
const PICKER_OPTION_CLASS = "atcb-list-item";
const PICKER_CLOSE_SELECTOR = ".atcb-list-item-close";
const APPLE_OPTION_ID_SUFFIX = "-apple";
const ESCAPE_KEY = "Escape";

const userAgent = () =>
  typeof navigator !== "undefined" ? navigator.userAgent || "" : "";

const isIOS = () => {
  const ua = userAgent();
  if (/MSStream/i.test(ua)) return false;
  return (
    /iPad|iPhone|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) &&
      typeof navigator !== "undefined" &&
      (navigator.maxTouchPoints || 0) > 1)
  );
};

/*
 * Technical Detail:
 * Only real Safari should open the calendar file directly. Every iOS
 * browser's user agent ends in "Safari" because they all use WebKit, and
 * Edge also includes "Version/", so matching "Safari" alone would treat
 * Edge, Opera and the others as Safari and leave them on the library's
 * Apple-calendar path. Require the Version/…Safari pair real Safari sends,
 * and reject the known third-party tokens (CriOS, FxiOS, EdgiOS, OPiOS,
 * GSA, DuckDuckGo).
 */
const isSafari = () =>
  /Version\/[\d.]+.*Safari/i.test(userAgent()) &&
  !/(?:CriOS|FxiOS|EdgiOS|OPiOS|GSA|DuckDuckGo)/i.test(userAgent());

const isWebView = () =>
  /; ?wv|(?:iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent());

/* True for any real iOS browser (Safari or third-party); false for in-app
 * webviews and every non-iOS platform. */
export const isIosBrowser = () => isIOS() && !isWebView();

/* True for iOS Chrome / Firefox / Edge / other third-party browsers; false
 * for Safari, in-app webviews and every non-iOS platform. */
export const needsIosCalendarFallback = () => isIosBrowser() && !isSafari();

const toIcsFileName = (calendarConfig) =>
  `${calendarConfig.iCalFileName || ICS_FILE_NAME_FALLBACK}${ICS_FILE_EXTENSION}`;

/* Same anchor click add-to-calendar-button 2.9.1 fired on iOS Safari: a
 * data: URL with the calendar MIME type, which Safari opens in Calendar. */
const openIcsDataUrl = (icsContent, fileName) => {
  const anchor = document.createElement("a");
  anchor.rel = "noopener";
  anchor.href = `${ICS_DATA_URL_PREFIX}${encodeURIComponent(icsContent)}`;
  anchor.target = "_self";
  anchor.download = fileName;
  anchor.dispatchEvent(
    new MouseEvent("click", {
      view: window,
      button: 0,
      bubbles: true,
      cancelable: false,
    }),
  );
};

const saveIcsBlob = (icsContent, fileName) => {
  FileSaver.saveAs(new Blob([icsContent], { type: ICS_MIME_TYPE }), fileName);
};

export const downloadIcsFile = (calendarConfig) => {
  const icsContent = buildIcsFileContent(calendarConfig);
  const fileName = toIcsFileName(calendarConfig);
  if (isSafari()) {
    openIcsDataUrl(icsContent, fileName);
    return;
  }
  saveIcsBlob(icsContent, fileName);
};

/* The picker lives in the library's open shadow root, so a listener on
 * `document` only sees it through composedPath(). */
const findPickerAppleOption = (event) => {
  if (typeof event.composedPath !== "function") return null;
  return (
    event.composedPath().find(
      (node) =>
        node instanceof Element &&
        node.classList.contains(PICKER_OPTION_CLASS) &&
        typeof node.id === "string" &&
        node.id.endsWith(APPLE_OPTION_ID_SUFFIX),
    ) || null
  );
};

const closeCalendarPicker = (optionElement) => {
  const root = optionElement.getRootNode();
  const closeControl =
    root && typeof root.querySelector === "function"
      ? root.querySelector(PICKER_CLOSE_SELECTOR)
      : null;
  if (closeControl) {
    closeControl.click();
    return;
  }
  /* Non-modal list styles have no close entry; the library closes on Escape. */
  document.dispatchEvent(new KeyboardEvent("keyup", { key: ESCAPE_KEY }));
};

/*
 * Intercept the "Apple" entry of the library's platform picker on iOS
 * third-party browsers: the library has no hook for it, so a capture-phase
 * listener on `document` stops the click before the library's own handler
 * runs, downloads the .ics and closes the picker. Returns the cleanup.
 */
export const installIosAppleCalendarIntercept = (getCalendarConfig) => {
  if (typeof document === "undefined") return () => {};

  const handleCaptureClick = (event) => {
    if (!needsIosCalendarFallback()) return;
    const appleOption = findPickerAppleOption(event);
    if (!appleOption) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    downloadIcsFile(getCalendarConfig());
    closeCalendarPicker(appleOption);
  };

  document.addEventListener("click", handleCaptureClick, { capture: true });
  return () =>
    document.removeEventListener("click", handleCaptureClick, {
      capture: true,
    });
};
